/**
 * Which routes need the premium workshop stylesheet (2026-09-09).
 *
 * `client/src/styles/cowork-ds.css` is 448 KB of source (74 KB gzipped) and is
 * scoped entirely to `.ds-cowork` / `.ds-sales` / `.ds-workshops`. It used to be
 * `@import`ed from `client/src/index.css`, which put it in the one render-blocking
 * stylesheet every page downloads — for the ~3 routes that actually use it.
 * It is now its own build entry (see `vite.config.ts`), linked from the
 * server-rendered <head> only on the routes below (see `server/premium-css.ts`)
 * and pulled in on demand during client-side navigation (see
 * `client/src/lib/premium-css.ts`).
 *
 * THIS LIST IS THE SOURCE OF TRUTH FOR THE SERVER. If you add `ds-cowork` to a
 * `pageClass` in `PREMIUM_LAYOUT_BY_SERIES` (client/src/pages/event-detail.tsx),
 * add the series slug here too, or the page ships its first paint unstyled.
 * event-detail.tsx carries a dev-only assertion that shouts when the two drift.
 */

/** Series whose premium `pageClass` contains `ds-cowork`. */
export const DS_COWORK_SERIES: readonly string[] = [
  "claude-code-for-beginners-workshop",
  "claude-for-sales-teams-workshop",
  "claude-for-design-workshop",
  "claude-cowork-for-beginners-workshop",
  "claude-for-beginners-workshop",
  "ai-video-workshop-for-beginners",
];

export function isDsCoworkSeries(seriesSlug?: string | null): boolean {
  return !!seriesSlug && DS_COWORK_SERIES.includes(seriesSlug);
}

/**
 * Does this request need cowork-ds.css in the <head>?
 *
 * - `/workshops` (and the legacy `/events` alias) — the index page renders
 *   `ds-cowork ds-workshops` (client/src/pages/events.tsx).
 * - `/workshops/<slug>` — only when the resolved event's series is in
 *   DS_COWORK_SERIES. The slug may be a series slug OR a dated session slug
 *   (`…-september-15-2026`), so the decision is keyed on the RESOLVED
 *   `seriesSlug`, never on the URL.
 *
 * `/workshops/<slug>/success` deliberately does not match — the thank-you page
 * is its own component and carries no ds- scope.
 */
export function needsPremiumCss(pathname: string, seriesSlug?: string | null): boolean {
  const path = pathname !== "/" && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (path === "/workshops" || path === "/events") return true;
  // Instructor bio pages and the VIP 1:1 request page (2026-10-13) render
  // `ds-cowork ds-vivid ds-ins`.
  if (path === "/instructors" || path.startsWith("/instructors/") || path === "/vip-session") return true;
  // The NYF × AdVenture workshop sign-up page (2026-10-20) renders `ds-cowork ds-vivid`.
  if (path === "/nyf") return true;
  if (/^\/(?:workshops|events)\/[^/]+$/.test(path)) return isDsCoworkSeries(seriesSlug);
  return false;
}
